import React from 'react';

import { DealType } from '../../../../../gql/generated/graphql';
import { DealStateEnum } from '../../../../../utils/types/deal';
import { DOWN_PAYMENT_STATES, DealStateMap, DealWithFarthestState } from '../../../utils';
import ReferralProgram from '../../referral/ReferralProgram';
import Header from '../Header';
import StepperDocumentsCard from '../StepperDocumentsCard';
import StepperDownPaymentCard from '../StepperDownPaymentCard';
import StepperLayout, { StepperSidebarItem } from '../StepperLayout';
import LeaseEndTeamCard from './LeaseEndTeamCard';
import PostSigningDealStepper from './PostSigningDealStepper';
import VehicleSummaryCard from './VehicleSummaryCard';

type PostSigningStepperDashboardProps = {
  deal: DealWithFarthestState;
};

const getHeaderContent = (deal: DealWithFarthestState): { header: string; subtitle: string } => {
  const isRefi = deal?.type === DealType.Refi;
  const isBooted = deal?.state === DealStateEnum.Booted || !!deal?.request_boot;
  const isFinalized = deal?.farthestState === DealStateEnum.Finalized || isBooted;

  if (isFinalized) {
    return isRefi
      ? { header: 'Your Refinance is Complete', subtitle: 'The car is officially yours' }
      : { header: 'Your Lease is Over!', subtitle: 'The car is officially yours' };
  }
  return {
    header: isRefi ? 'Completing Your Refinance' : 'Completing Your Buyout',
    subtitle: "Here's what we're working on behind the scenes",
  };
};

const PostSigningStepperDashboard = ({ deal }: PostSigningStepperDashboardProps) => {
  const showDownPayment =
    (deal?.financial_info?.money_down ?? 0) > 0 &&
    DOWN_PAYMENT_STATES.includes(deal?.state as DealStateEnum) &&
    DealStateMap[deal?.state as DealStateEnum] <= DealStateMap[DealStateEnum.Funded];

  const { header, subtitle } = getHeaderContent(deal);

  const sidebarItems: StepperSidebarItem[] = [
    { key: 'vehicle-summary', node: <VehicleSummaryCard deal={deal} /> },
    ...(showDownPayment
      ? [
          {
            key: 'down-payment',
            node: <StepperDownPaymentCard moneyDown={deal?.financial_info?.money_down ?? 0} />,
          },
        ]
      : []),
    { key: 'documents', node: <StepperDocumentsCard deal={deal} /> },
    { key: 'referral', node: <ReferralProgram deal={deal} /> },
    { key: 'lease-end-team', node: <LeaseEndTeamCard deal={deal} /> },
  ];

  return (
    <StepperLayout
      header={
        <Header
          header={header}
          subtitle={subtitle}
          isCobuyer={deal?.isCobuyer}
          isSoftClose={false}
        />
      }
      sidebarItems={sidebarItems}
    >
      <PostSigningDealStepper deal={deal} />
    </StepperLayout>
  );
};

export default PostSigningStepperDashboard;
